import { initOptional } from '../common/miscUtils';

const valueKey = '_value';

const createStorageGroup = <T extends Record<string, any>>(target: T) => {
  return new Proxy(target, {
    get(_, prop) {
      const unparsedValue = localStorage.getItem(String(prop));

      if (unparsedValue === null) return undefined;

      try {
        const parsedValue = JSON.parse(unparsedValue);
        return parsedValue[valueKey];
      } catch {
        // eslint-disable-next-line no-console
        console.error(`Failed to retrieve local storage value for key ${String(prop)}`);

        return undefined;
      }
    },
    set(_, prop, value) {
      try {
        localStorage.setItem(String(prop), JSON.stringify({ [valueKey]: value }));
        return true;
      } catch {
        return false;
      }
    },
    deleteProperty(_, prop) {
      try {
        localStorage.removeItem(String(prop));
        return true;
      } catch {
        return false;
      }
    },
  });
};

const mergeInProxy = <T extends Record<string, any>, ProxyObject extends Record<string, any>>(
  target: T,
  proxyObject: ProxyObject
) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const prop of Object.getOwnPropertyNames(proxyObject)) {
    Object.defineProperty(target, prop, {
      get() {
        return proxyObject[prop];
      },
      set(value) {
        (proxyObject as any)[prop] = value;
      },
      enumerable: true,
      configurable: true,
    });
  }

  return target as T & ProxyObject;
};

const auth = createStorageGroup({
  milesTokenValue: initOptional<string>(),
  milesRefreshTokenValue: initOptional<string>(),
  milesTokenExpirationDate: initOptional<number>(),
  milesRefreshTokenExpirationDate: initOptional<number>(),
  codeChallenge: initOptional<string>(),
  codeVerifier: initOptional<string>(),
  redirectUri: initOptional<string>(),
});

const restore = createStorageGroup({
  cachedReduxState: initOptional<string>(),
  cachedPathname: initOptional<string>(),
});

const dashboardOnfidoFlow = createStorageGroup({
  postOnfidoDashboardRedirect: initOptional<string>(),
});

/**
 * - Global, not namespaced local storage fields.
 */
const global = createStorageGroup({
  manualLogInAsCompanyInProgress: initOptional<boolean>(),
  /**
   * - Company for which dealer would like to be automatically authorized on application load.
   */
  favouriteCompanyId: initOptional<string>(),
  buildDate: initOptional<string>(),
});

/**
 * - Local storage helper proxy.
 */
export const storage = mergeInProxy(
  {
    auth,
    restore,
    dashboardOnfidoFlow,
  },
  global
);

/**
 * - It runs early on app bootstrap and ensures local storage will
 * be re-set if application version changed.
 * - It helps avoid complicated backward compatibility problems when logic
 * and/or structure of storage change accross builds.
 */
export const invalidateStorage = () => {
  const isLocalhost = window.location.href.includes('localhost');

  if (isLocalhost) return;

  const newBuildDate = process.env.REACT_APP_BUILD_DATE ?? 'Unknown build date';

  if (newBuildDate !== storage.buildDate) {
    localStorage.clear();
    storage.buildDate = newBuildDate;
    window.location.reload();
  }
};
